import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';

import AnimationController from '../../assets/js/controllers/animation';
import DetectController from '../../assets/js/controllers/detect';
import ResizeController from '../../assets/js/controllers/resize';
import ScrollController from '../../assets/js/controllers/scroll';
import SkrolliteController from '../../assets/js/controllers/skrollite';

import { getRootOffset } from '../../assets/js/helpers';

export default class Quotes {

	static selector = 'quotes';

	isAutoplay = false;
	isInViewport = false;
	isDesktop;

	animation;

	el = {};
	bounds = {};

	quotes = [];

	active = 0;

	interval;

	constructor(container) {
		this.el = {
			container,
			slideWrap: container.querySelector(`.${this.constructor.selector}__slide-wrap`),
			slides: container.querySelectorAll(`.${this.constructor.selector}__slide`),
			paginationWrap: container.querySelector(`.${this.constructor.selector}__pagination-wrap`),
			pagination: container.querySelectorAll(`.${this.constructor.selector}__pagination ul li`)
		};

		this.isDesktop = DetectController.isDesktop;

		if (this.el.slides.length > 1) {
			for (const [i, slide] of this.el.slides.entries()) {
				const text = slide.querySelector(`.${this.constructor.selector}__slide-text`);
				const citation = slide.querySelector(`.${this.constructor.selector} cite`);

				this.quotes.push({
					text,
					citation,
					splitLines: new SplitText(text, {
						type: 'lines'
					}),
					height: slide.scrollHeight
				});

				gsap.set(this.quotes[i].splitLines.lines, {
					y: '102%'
				});

				gsap.set(this.quotes[i].citation, {
					autoAlpha: 0
				});

				for (const line of this.quotes[i].splitLines.lines) {
					const lineWrapper = document.createElement('div');
					lineWrapper.appendChild(line);
					text.appendChild(lineWrapper);
				}
			}

			if (this.isDesktop) {
				this.measure();
				this.start();
			} else {
				this.showQuote();
			}

			this.addEventListeners();
		}
	}

	measure() {
		this.bounds.top = getRootOffset(this.el.container).top;
		this.bounds.height = this.el.container.offsetHeight;
		this.bounds.winHeight = window.innerHeight;
	}

	addEventListeners() {
		for (const pagination of this.el.pagination) {
			pagination.addEventListener('click', this.#onPaginationClick);
		}

		if (this.isDesktop) ResizeController.add(this.#onWindowResize);
	}

	removeEventListeners() {
		for (const pagination of this.el.pagination) {
			pagination.removeEventListener('click', this.#onPaginationClick);
		}

		if (this.isDesktop) ResizeController.remove(this.#onWindowResize);
	}

	#onPaginationClick = (e) => {
		if (this.interval) {
			this.isAutoplay = true;
			this.interval.kill();
			this.interval = null;
		}

		if (!this.isAnimating) {
			const { currentTarget } = e;
			const currentIndex = [...this.el.paginationWrap.children].indexOf(currentTarget);
			if (this.active === currentIndex) return;

			this.hideQuote();

			this.active = currentIndex;

			this.showQuote();
		}
	}

	#onWindowResize = () => {
		this.measure();
	}

	hideQuote() {
		this.isAnimating = true;
		this.el.pagination[this.active].classList.remove('is-active');
		this.timelineHide = null;
		this.timelineHide = gsap.timeline({});
		this.timelineHide
			.fromTo(this.quotes[this.active].splitLines.lines, {
				y: '0%'
			}, {
				ease: 'power3.inOut',
				duration: 1,
				stagger: 0.06,
				y: '-102%'
			}, 'start')
			.fromTo(this.quotes[this.active].citation, {
				autoAlpha: 1
			}, {
				autoAlpha: 0,
				ease: 'power3.inOut',
				duration: 0.5
			}, 'start');
	}

	showQuote() {
		this.el.pagination[this.active].classList.add('is-active');
		this.timelineShow = null;
		this.timelineShow = gsap.timeline({
			delay: this.isAutoplay || !this.isDesktop ? 0.5 : 0,
			onComplete: () => {
				this.isAnimating = false;
			}
		});
		this.timelineShow
			.to(this.el.slideWrap, {
				ease: 'power3.inOut',
				height: `${this.quotes[this.active].height}px`,
				duration: 1,
				onComplete: () => {
					ScrollController.adjust();
					SkrolliteController.adjust();
				}
			}, 'start')
			.fromTo(this.quotes[this.active].splitLines.lines, {
				y: '102%'
			}, {
				ease: 'power3.inOut',
				duration: 1,
				stagger: 0.06,
				y: '0%'
			}, 'start')
			.fromTo(this.quotes[this.active].citation, {
				autoAlpha: 0
			}, {
				autoAlpha: 1,
				ease: 'power3.inOut',
				duration: 0.5,
			}, 'start+=0.5');
	}

	autoplay() {
		this.isAutoplay = true;

		if (this.interval) this.interval.kill();
		this.interval = gsap.timeline({
			repeat: -1
		});

		this.interval.call(() => {
			if (this.quotes[this.active]) {
				this.hideQuote();
			}

			if (this.active < this.quotes.length - 1) {
				this.active += 1;
			} else {
				this.active = 0;
			}

			if (this.quotes[this.active]) {
				this.showQuote();
			}
		}, null, '+=6');
	}

	start() {
		this.animation = AnimationController.set({
			update: this.#update,
			render: this.#render
		});
	}

	stop() {
		AnimationController.clear(this.animation);
	}

	#update = () => {
		if (ScrollController.top + this.bounds.winHeight > this.bounds.top
			&& ScrollController.top < this.bounds.top + this.bounds.height) {
			return true;
		}
		if (this.interval) {
			this.interval.kill();
			this.interval = null;
		}
	}

	#render = () => {
		if (!this.isInViewport) {
			this.isInViewport = true;
			this.showQuote();
			this.autoplay();
		}
	}

	destroy() {
		if (this.interval) this.interval.kill();
		this.stop();
		this.removeEventListeners();
	}

}
